import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import type { RdmMessage } from "./rdmMessage.types";

export interface UseSharedRdmWebsocketResult {
  isLeader: boolean;
  lastMessage: RdmMessage | null;
}

// react-use-websocket@3 (the latest that supports React 16) has no `heartbeat`,
// so we keep AWS API Gateway's idle timer fresh by sending an app-level "ping"
// text frame ourselves while the socket is open. The server echoes "pong"; both
// are filtered out below so they never reach lastJsonMessage.
const KEEPALIVE = {
  message: "ping",
  returnMessage: "pong",
  interval: 15_000,
} as const;

const BROADCAST_NAME = "rdm_ws_events";
const LOCK_NAME = "rdm_ws_lock";

/** Present in Chromium; omitted in jsdom and some browsers. */
function getNavigatorLocks(): Pick<LockManager, "request"> | undefined {
  return (navigator as Navigator & { locks?: LockManager }).locks;
}

export function useSharedRdmWebsocket(
  baseUrl: string,
  token: string | null,
): UseSharedRdmWebsocketResult {
  const [isLeader, setIsLeader] = useState(false);
  const isLeaderRef = useRef(isLeader);
  useEffect(() => {
    isLeaderRef.current = isLeader;
  }, [isLeader]);

  const channel = useMemo(() => new BroadcastChannel(BROADCAST_NAME), []);

  useEffect(() => {
    return () => {
      channel.close();
    };
  }, [channel]);

  // Cache the last non-null token for `queryParams`. A rotated token changes the
  // appended `?token=` and reconnects (queryParams is a connect dependency); a
  // brief expiry gap keeps the last good token so we don't reconnect with null.
  const tokenRef = useRef(token);
  if (token) tokenRef.current = token;

  // First-connect gate; never flips back on the brief expiry gap to avoid a
  // needless reconnect.
  const [hasToken, setHasToken] = useState(Boolean(token));
  useEffect(() => {
    if (token) setHasToken(true);
  }, [token]);

  // Normalize to a trailing slash so the API Gateway stage path is unchanged;
  // `queryParams` appends the `?token=...`.
  const socketUrl = useMemo(
    () => (baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`),
    [baseUrl],
  );

  useEffect(() => {
    const locks = getNavigatorLocks();
    if (!locks) {
      setIsLeader(true);
      return;
    }

    const ac = new AbortController();

    locks
      .request(LOCK_NAME, { signal: ac.signal }, () => {
        setIsLeader(true);
        return new Promise<void>((resolve) => {
          ac.signal.addEventListener("abort", () => resolve());
        });
      })
      .catch(() => {});

    return () => ac.abort();
  }, []);

  const relayToFollowers = useCallback(
    (data: RdmMessage) => {
      if (!isLeaderRef.current) return;
      channel.postMessage(data);
    },
    [channel],
  );

  const { lastJsonMessage, sendMessage, readyState } = useWebSocket(
    isLeader && hasToken ? socketUrl : null,
    {
      // Token is read only at $connect; queryParams appends `?token=...`.
      queryParams: tokenRef.current ? { token: tokenRef.current } : undefined,
      share: true,
      onReconnectStop: () => {
        console.error(
          "[RDM] WebSocket reconnect stopped, and cannot be resumed",
        );
      },
      // Keep keepalive echoes out of lastMessage; else lastJsonMessage parses
      // "pong" and surfaces its UNPARSABLE_JSON_OBJECT sentinel ({}).
      filter: (event: MessageEvent<string>) =>
        event.data !== KEEPALIVE.message &&
        event.data !== KEEPALIVE.returnMessage,
      onMessage: (event: MessageEvent<string>) => {
        // Skip keepalive echoes; guard parse so a bad frame can't break relay.
        if (
          event.data === KEEPALIVE.message ||
          event.data === KEEPALIVE.returnMessage
        ) {
          return;
        }
        try {
          relayToFollowers(JSON.parse(event.data) as RdmMessage);
        } catch {
          /* non-JSON frame — nothing to relay */
        }
      },
      onError: (event: ErrorEvent) => {
        console.error("[RDM] WebSocket error", event);
      },
      onOpen: () => {
        console.log("[RDM] WebSocket connected");
      },
      onClose: (event: CloseEvent) => {
        // 1005/1006 are expected on reconnects + AWS's 2h cap; keep them quiet.
        const isExpected = event.code === 1005 || event.code === 1006;
        const log = isExpected ? console.debug : console.log;
        log("[RDM] WebSocket disconnected", event);
      },
      shouldReconnect: () => true,
      // react-use-websocket@3 only supports a fixed interval (no backoff fn). Keep
      // it positive: a 0ms interval would exhaust all attempts in milliseconds.
      reconnectInterval: 5_000,
      reconnectAttempts: 100,
    },
  );

  // Manual keepalive (react-use-websocket@3 has no heartbeat): ping while open to
  // keep AWS's idle timer fresh (see KEEPALIVE).
  useEffect(() => {
    if (readyState !== ReadyState.OPEN) return;
    const id = setInterval(
      () => sendMessage(KEEPALIVE.message),
      KEEPALIVE.interval,
    );
    return () => clearInterval(id);
  }, [readyState, sendMessage]);

  const [followerMessage, setFollowerMessage] = useState<RdmMessage | null>(
    null,
  );

  useEffect(() => {
    if (!token) return;
    console.log("[RDM] isLeader", isLeader);

    if (isLeader) return;

    const onMessage = (event: MessageEvent<RdmMessage>) => {
      setFollowerMessage(event.data);
    };

    channel.addEventListener("message", onMessage);
    return () => channel.removeEventListener("message", onMessage);
  }, [channel, isLeader, token]);

  const lastMessage = isLeader ? lastJsonMessage : followerMessage;

  useEffect(() => {
    if (!lastMessage) return;
    console.log("[RDM] Message", lastMessage);
  }, [lastMessage]);

  return { isLeader, lastMessage };
}
